'use client';

import {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
} from 'react';
import { Handle, Position, type NodeProps } from '@xyflow/react';
import { useT } from '@/components/i18n-provider';
import { cn } from '@/lib/utils';
import {
  useWorkspaceActions,
  useWorkspaceItem,
} from '@/components/workspace/store';
import { ResizeEdge } from '@/components/workspace/resize-edge';
import { useNodeResize } from '@/components/workspace/use-node-resize';
import { AnnotationSelectionChrome } from '@/components/workspace/annotation-selection-chrome';
import { AnnotationToolbar } from '@/components/workspace/annotation-toolbar';
import { Markdown } from '@/components/markdown';
import { lookupIframeEmbed } from '@/lib/content/iframe-embeds';
import { importUrlAsExternalWidget } from '@/components/workspace/paste-handler';
import type {
  AnnotationColor,
  AnnotationKind,
  AnnotationShape,
  AnnotationStroke,
  WorkspaceItemDTO,
} from '@/lib/workspaces/types';

export type AnnotationNodeData = { itemId: string };

// -----------------------------------------------------------------------
// Palette
//
// Sticky / shape colors are stored as palette keys (not raw hex) so they
// resolve consistently across light + dark themes. Tints live in OKLCH-ish
// pastels — saturated enough to read on a light canvas but not loud enough
// to drown widget chrome. The dark theme branch shifts each tint to a
// muted, slightly desaturated mid-tone with a brighter accent ring so
// stickies pop without burning the eyes.
//
// `fg` is the readable text color over each tint; `ring` is the selection
// outline (a slightly stronger version of the bg used by .selected). The
// stroke color matches the ring for shape outlines.
// -----------------------------------------------------------------------
const PALETTE: Record<
  AnnotationColor,
  { bg: string; bgDark: string; fg: string; fgDark: string; ring: string; ringDark: string }
> = {
  // Foregrounds are intentionally near-black on light and near-white
  // on dark — earlier palettes used heavily-tinted text (e.g. a warm
  // brown #5A3F00 on yellow) that registered as "orange" rather than
  // "ink", and against the dark-themed background the warm hues
  // dropped below readable contrast. Keeping the FG mostly neutral
  // (with just a faint palette tint on dark) preserves the per-color
  // identity via `bg`+`ring` without sacrificing legibility.
  yellow: {
    bg: '#FEF3C7',
    bgDark: '#5A4A0E',
    fg: '#1A1408',
    fgDark: '#FFFBE6',
    ring: '#F59E0B',
    ringDark: '#FBBF24',
  },
  green: {
    bg: '#DCFCE7',
    bgDark: '#10371F',
    fg: '#0A1A12',
    fgDark: '#ECFDF3',
    ring: '#22C55E',
    ringDark: '#4ADE80',
  },
  pink: {
    bg: '#FCE7F3',
    bgDark: '#4A1031',
    fg: '#1F0612',
    fgDark: '#FDF2F8',
    ring: '#EC4899',
    ringDark: '#F472B6',
  },
  blue: {
    bg: '#DBEAFE',
    bgDark: '#1B3553',
    fg: '#08122A',
    fgDark: '#EFF6FF',
    ring: '#3B82F6',
    ringDark: '#60A5FA',
  },
  violet: {
    bg: '#EDE9FE',
    bgDark: '#2D1B5C',
    fg: '#100624',
    fgDark: '#F5F3FF',
    ring: '#8B5CF6',
    ringDark: '#A78BFA',
  },
  amber: {
    bg: '#FFEDD5',
    bgDark: '#5B2912',
    fg: '#1F0E04',
    fgDark: '#FFF7ED',
    ring: '#F97316',
    ringDark: '#FB923C',
  },
  gray: {
    bg: '#F1F5F9',
    bgDark: '#283242',
    fg: '#0F172A',
    fgDark: '#F1F5F9',
    ring: '#64748B',
    ringDark: '#94A3B8',
  },
};

function paletteToStyle(
  color: AnnotationColor | undefined,
  fillEnabled: boolean = true,
): CSSProperties {
  const c = PALETTE[color ?? 'yellow'];
  // When fill is disabled we drive `--ann-bg` / `--ann-bg-dark` to
  // `transparent`. That keeps the SVG fill="var(--ann-bg)" pattern
  // working everywhere — no per-primitive branching — and lets the
  // stroke + text colours stay palette-driven so the user can still
  // pick a colour to tint the border / text without seeing a fill.
  return {
    ['--ann-bg' as string]: fillEnabled ? c.bg : 'transparent',
    ['--ann-bg-dark' as string]: fillEnabled ? c.bgDark : 'transparent',
    ['--ann-fg' as string]: c.fg,
    ['--ann-fg-dark' as string]: c.fgDark,
    ['--ann-ring' as string]: c.ring,
    ['--ann-ring-dark' as string]: c.ringDark,
  };
}

// A wide, clearly-stepped range (~5×) so Small → Extra large reads as a real
// size jump on stickies / text / shapes (the old 12→22 span was barely
// noticeable). The px values double as the base font-size on the markdown
// wrapper so headings (em-based in `prose`) scale together — see FONT_PX.
type FontToken = NonNullable<WorkspaceItemDTO['state']['cache']>['fontSize'] & string;
const FONT_PX: Record<FontToken, number> = { sm: 13, md: 24, lg: 42, xl: 64 };
const FONT_SIZE: Record<FontToken, string> = {
  sm: 'text-[13px] leading-snug',
  md: 'text-[24px] leading-snug',
  lg: 'text-[42px] leading-tight',
  xl: 'text-[64px] leading-tight',
};

function strokeDashArray(stroke: AnnotationStroke | undefined): string | undefined {
  switch (stroke) {
    case 'dashed':
      return '8 6';
    case 'dotted':
      return '2 4';
    default:
      return undefined;
  }
}

/**
 * Compute the SVG stroke colour for a given stroke style. We collapse
 * `'none'` into `transparent` rather than removing the `stroke` attr
 * entirely — keeps the primitive's hit-test area unchanged so a
 * user-perceived "borderless rectangle" still receives clicks across
 * its full painted area.
 */
function strokeColor(stroke: AnnotationStroke | undefined, color: string): string {
  return stroke === 'none' ? 'transparent' : color;
}

/**
 * Four cardinal connection handles. Matches the simplified single-
 * handle-per-side model used by NodeFrame — each handle is `type:
 * "source"` with `isConnectableEnd` so it accepts both ends of a
 * connection. The CSS rules in `globals.css` (`.ws-conn-handle`)
 * keep them invisible until the canvas enters connector mode
 * (`.ws-canvas-tool`) or the node is hovered.
 *
 * Handle ids stay one-letter (`t/r/b/l`) so the connector item's
 * persisted `fromHandle`/`toHandle` cache fields match across widget
 * frames and annotation nodes — connectors can be drawn between any
 * pair of nodes without us special-casing the source / target type.
 */
function AnnotationHandles() {
  return (
    <>
      <Handle
        id="t"
        type="source"
        position={Position.Top}
        className="ws-conn-handle ws-conn-handle-t"
        isConnectableEnd
      />
      <Handle
        id="r"
        type="source"
        position={Position.Right}
        className="ws-conn-handle ws-conn-handle-r"
        isConnectableEnd
      />
      <Handle
        id="b"
        type="source"
        position={Position.Bottom}
        className="ws-conn-handle ws-conn-handle-b"
        isConnectableEnd
      />
      <Handle
        id="l"
        type="source"
        position={Position.Left}
        className="ws-conn-handle ws-conn-handle-l"
        isConnectableEnd
      />
    </>
  );
}

// -----------------------------------------------------------------------
// AnnotationNode
//
// Single React Flow node component for all three annotation kinds. The
// renderer switches on `cache.kind` so converting between sticky / text /
// shape is a server-side patch with no remount churn — selection, edit
// mode, and contenteditable focus all survive the conversion.
//
// Interaction model (see plan Phase 2):
//   - single click → React Flow selects the node; the whole surface is
//     the drag handle (no chrome).
//   - double click → enter edit mode. RF dragging is disabled while
//     editing via the `nodrag` class so a click-to-place-cursor doesn't
//     start a pan.
//   - Esc / blur / click outside → commit + exit edit mode.
// -----------------------------------------------------------------------
function AnnotationNodeImpl({ data, selected }: NodeProps) {
  const itemId = (data as AnnotationNodeData).itemId;
  const item = useWorkspaceItem(itemId);
  const { patchItemState } = useWorkspaceActions();

  // Resize gesture handlers shared with `NodeFrame`. Per-kind min sizes
  // are passed below; the 24px snap step matches the dot grid.
  const resize = useNodeResize({
    itemId,
    minWidth: 64,
    minHeight: 28,
    snapStep: 24,
  });

  const cache = (item?.state.cache ?? {}) as NonNullable<WorkspaceItemDTO['state']['cache']>;
  const kind: AnnotationKind = (cache.kind as AnnotationKind | undefined) ?? 'sticky';
  const text = (cache.text as string | undefined) ?? '';
  const color = (cache.color as AnnotationColor | undefined) ?? 'yellow';
  const shape = (cache.shape as AnnotationShape | undefined) ?? 'rectangle';
  const stroke = (cache.strokeStyle as AnnotationStroke | undefined) ?? 'solid';
  const fontSize = (cache.fontSize as keyof typeof FONT_SIZE | undefined) ?? 'md';
  const fontWeight = (cache.fontWeight as 'regular' | 'bold' | undefined) ?? 'regular';
  // Shapes default to centred text (Figma / Figjam convention — labels
  // sit dead-centre inside a primitive) while stickies + text
  // annotations default to left-aligned because they read like
  // paragraph copy.
  const textAlign =
    (cache.textAlign as 'left' | 'center' | 'right' | undefined) ??
    (kind === 'shape' ? 'center' : 'left');
  // Vertical alignment: stickies default to top (paper grows down as
  // the user types); shapes default to middle (label sits centred
  // inside the primitive). Text annotations are auto-sized so the
  // distinction is invisible — kept at top for consistency.
  const verticalAlign =
    (cache.verticalAlign as 'top' | 'middle' | 'bottom' | undefined) ??
    (kind === 'shape' ? 'middle' : 'top');
  // `fillEnabled === false` (set by the toolbar's "No fill" option)
  // renders the annotation with a transparent body. Strokes / text
  // still render so the user can outline a region without obscuring
  // what's behind it. Default to true so existing items keep their
  // current appearance.
  const fillEnabled = cache.fillEnabled !== false;
  const isEditing = item?.state.editing === true;

  const editorRef = useRef<HTMLDivElement | null>(null);
  // We mirror the current text in a ref so the callback ref below
  // can read it without having to invalidate (and re-fire) every
  // time `text` changes — invalidating would defeat the purpose of
  // the callback ref since React would unmount/remount with a fresh
  // empty DOM node on every keystroke.
  const textRef = useRef(text);
  textRef.current = text;

  // ---- ref-managed contentEditable ------------------------------------
  //
  // The text content of the editor lives in the DOM, not in React's
  // virtual DOM. React's reconciliation does NOT play nicely with
  // contentEditable: when the browser inserts `<br>` and wraps text
  // chunks while the user types, React can't tell what's "its" content
  // and what the user added, so flipping `contentEditable` off and
  // letting React render `{text}` as children produces phantom
  // duplicates (the "sticky shows text twice after blur" bug).
  //
  // Strategy:
  //   1. React renders the editor as an empty `<div ref>` — never any
  //      children, regardless of edit mode.
  //   2. A callback ref syncs `el.innerText = text` whenever the DOM
  //      node attaches AND on every render where the latest text
  //      doesn't match what's in the DOM. Skipping focused syncs
  //      preserves the user's caret while typing.
  //   3. The placeholder (`Note…` / `Text`) renders as a SEPARATE
  //      absolutely-positioned span behind the editor, gated on empty
  //      text + not editing. Keeping the placeholder out of the
  //      contentEditable subtree avoids any chance of React injecting
  //      it as a child that contentEditable would then absorb on edit.
  //
  // Why a callback ref and not a layout effect: when the user converts
  // kind (sticky → text → shape) the editor's parent DOM chain
  // changes, so React unmounts the old contentEditable div and
  // mounts a fresh one. `useLayoutEffect` only re-runs when its
  // declared deps change — `text` didn't, so the new (empty) div
  // never got re-seeded with the existing text and the user saw
  // "text disappeared until refresh". A callback ref runs on every
  // attach, so the conversion remount path lands the text back in
  // the new DOM node immediately.
  const syncEditor = useCallback((node: HTMLDivElement | null) => {
    editorRef.current = node;
    if (!node) return;
    if (document.activeElement === node) return;
    const next = textRef.current;
    if (node.innerText !== next) node.innerText = next;
  }, []);

  // Mirror-sync when `text` changes WITHOUT a remount (e.g. an external
  // store update, collaborative edit, or undo). The callback ref above
  // handles the remount path (kind switch); this effect handles the
  // "same DOM node, different text" path. We skip while focused so we
  // never stomp on the caret position mid-typing.
  useEffect(() => {
    const el = editorRef.current;
    if (!el) return;
    if (document.activeElement === el) return;
    if (el.innerText !== text) el.innerText = text;
  }, [text]);

  // ---- text commit ------------------------------------------------------
  const commitText = useCallback(() => {
    const el = editorRef.current;
    if (!el) return;
    const next = el.innerText.replace(/\u00A0/g, ' '); // unwrap nbsp
    if (next === text) return;
    patchItemState(itemId, { cache: { text: next } }, { debounceMs: 250 });
  }, [itemId, patchItemState, text]);

  const enterEdit = useCallback(() => {
    if (isEditing) return;
    patchItemState(itemId, { editing: true }, { persist: false });
  }, [itemId, isEditing, patchItemState]);

  const exitEdit = useCallback(() => {
    commitText();
    if (isEditing) {
      patchItemState(itemId, { editing: false }, { persist: false });
    }
  }, [commitText, isEditing, itemId, patchItemState]);

  // Focus + caret-to-end when edit mode is entered. Text content is
  // already in the DOM from the layout effect above; we just place the
  // caret so typing appends rather than starting at index 0.
  useEffect(() => {
    if (!isEditing) return;
    const el = editorRef.current;
    if (!el) return;
    el.focus();
    const range = document.createRange();
    range.selectNodeContents(el);
    range.collapse(false);
    const sel = window.getSelection();
    sel?.removeAllRanges();
    sel?.addRange(range);
  }, [isEditing]);

  // ---- Figma-jam-style key-to-edit ---------------------------------------
  // While the node is selected (but not yet in edit mode), tapping any
  // printable key flips into edit mode and starts typing. Matches the
  // Figma / Figma-jam mental model where a selected sticky is "armed".
  useEffect(() => {
    if (!selected || isEditing) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (e.key.length !== 1) return; // ignore non-printable
      e.preventDefault();
      enterEdit();
      requestAnimationFrame(() => {
        const el = editorRef.current;
        if (!el) return;
        el.innerText = (text ?? '') + e.key;
        const range = document.createRange();
        range.selectNodeContents(el);
        range.collapse(false);
        const sel = window.getSelection();
        sel?.removeAllRanges();
        sel?.addRange(range);
      });
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [selected, isEditing, text, enterEdit]);

  if (!item) return null;

  const styleVars = paletteToStyle(color, fillEnabled);
  const fontCls = cn(
    'font-annotation',
    FONT_SIZE[fontSize],
    fontWeight === 'bold' ? 'font-semibold' : 'font-normal',
    textAlign === 'center' && 'text-center',
    textAlign === 'right' && 'text-right',
    textAlign === 'left' && 'text-left',
  );
  // Vertical alignment is applied on the contenteditable div via flex.
  // Modern browsers happily run contenteditable with `display: flex`
  // and the caret keeps positioning correctly across lines — keeps
  // the editor area click-anywhere even when the text block is
  // anchored to the top / middle / bottom of the box.
  const vAlignCls = cn(
    'flex flex-col',
    verticalAlign === 'middle' && 'justify-center',
    verticalAlign === 'bottom' && 'justify-end',
    verticalAlign === 'top' && 'justify-start',
  );

  // Shared editor surface.
  //
  // Two render modes:
  //   - Editing: the ref-managed contentEditable div is shown so the
  //     user can type raw markdown source. The div is always EMPTY in
  //     React's virtual DOM — never any children — so React can't
  //     reconcile against the DOM the browser's contentEditable
  //     mutates. Text is synced via the layout effect above
  //     (`el.innerText = text`).
  //   - Viewing: the contentEditable is hidden and we render the
  //     stored `text` through the shared `Markdown` component so
  //     headings / lists / inline code / links render properly.
  //     Setting `font-size` on the wrapper drives `prose`'s em-based
  //     scale so every markdown element scales together when the user
  //     changes `cache.fontSize`.
  //
  // The placeholder ("Note…" / "Text") is a sibling positioned behind
  // the editor, visible only when `!text && !isEditing`.
  const placeholderLabel =
    kind === 'sticky' ? 'Note…' : kind === 'text' ? 'Text' : '';
  const editorSurface = (
    <div className="relative h-full w-full">
      {!text && !isEditing && placeholderLabel ? (
        <span
          aria-hidden
          className={cn(
            'pointer-events-none absolute inset-0 select-none',
            'text-current opacity-40',
            fontCls,
          )}
        >
          {placeholderLabel}
        </span>
      ) : null}
      {!isEditing && text ? (
        <div
          // No `nodrag` here — we want the whole markdown body to act
          // as the drag handle (matches the empty-sticky behavior).
          // React Flow still treats a quick pointerdown/up without
          // movement as a click, so inline <a> links inside the
          // rendered markdown remain navigable. `select-none` keeps
          // text-selection from competing with the drag gesture. The
          // parent owns overflow + nowheel; we just handle typography.
          //
          // Base font-size is set INLINE (not via `prose-sm`, which would
          // pin it to 0.875rem and defeat the size picker). `prose`'s
          // headings/lists are em-based, so they scale off this base —
          // Small → Extra large now visibly resizes headings + body alike.
          style={{ fontSize: `${FONT_PX[fontSize]}px` }}
          className={cn(
            'relative h-full w-full select-none break-words',
            fontCls,
            vAlignCls,
            'prose max-w-none dark:prose-invert',
            // Tight rhythm so headings / lists / paragraphs don't
            // introduce big vertical gaps inside small sticky bodies.
            'prose-headings:font-semibold prose-headings:my-1',
            'prose-p:my-1 prose-ul:my-1 prose-ol:my-1 prose-pre:my-1',
            // Inherit the annotation foreground via ws-annotation-fg
            // on the parent — disable prose's hard-coded text colors.
            'prose-headings:text-current prose-p:text-current prose-li:text-current',
            'prose-strong:text-current prose-em:text-current',
            'prose-code:text-current prose-blockquote:text-current',
          )}
        >
          <Markdown suppressLinkPreviews>{text}</Markdown>
        </div>
      ) : null}
      <div
        ref={syncEditor}
        contentEditable={isEditing}
        suppressContentEditableWarning
        onBlur={exitEdit}
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            e.preventDefault();
            (e.currentTarget as HTMLDivElement).blur();
          }
        }}
        onPaste={(e) => {
          e.preventDefault();
          const t = e.clipboardData.getData('text/plain');
          document.execCommand('insertText', false, t);
        }}
        className={cn(
          'outline-none whitespace-pre-wrap break-words h-full w-full',
          // While viewing, the contentEditable is just a hidden DOM
          // mirror used to seed the editor on next focus. It MUST
          // stay pointer-events-none and invisible so empty stickies
          // (and the area behind rendered markdown) don't intercept
          // mousedown — otherwise React Flow can't start a node /
          // group drag from the annotation body. `select-none` is
          // redundant under pointer-events:none but harmless.
          isEditing
            ? 'relative ws-selectable nodrag'
            : 'absolute inset-0 invisible pointer-events-none select-none',
          fontCls,
          vAlignCls,
        )}
        style={{ cursor: isEditing ? 'text' : 'inherit' }}
      />
    </div>
  );

  // Edit affordance — pencil-cursor outline that surrounds the editor
  // while edit mode is engaged. Keeps the user oriented inside a sticky
  // that doesn't have other chrome to anchor the focus.
  const editingRing =
    isEditing && (
      <div
        aria-hidden
        className="absolute inset-0 rounded-[inherit] pointer-events-none"
        style={{
          boxShadow:
            '0 0 0 2px color-mix(in srgb, var(--ann-ring) 70%, transparent)',
        }}
      />
    );

  // Figjam-style selection chrome — blue bounding box with corner
  // squares + edge midpoint ticks. Replaces the older palette-tinted
  // shadow ring so selection is unambiguous regardless of palette
  // colour (a yellow sticky's amber ring used to blend into the
  // bg). The shape variant feeds a 0 radius because its SVG primitive
  // owns the visual edge — a rectangular blue border around a
  // diamond / ellipse / triangle would read as a second containing
  // box rather than as selection chrome.
  const selectionRing = selected && !isEditing && (
    <AnnotationSelectionChrome rounded={kind === 'text' ? 4 : 0} />
  );

  // Floating Figjam-style toolbar above the selected annotation —
  // color picker, kind switch, shape picker (when kind === 'shape'),
  // font size, bold, delete. Renders via RF's NodeToolbar so it
  // tracks the node's screen position across pan / zoom and lives
  // above the canvas in a portal so it doesn't get clipped.
  //
  // `link` annotations don't expose this toolbar — none of its
  // controls (color, font size, bold, kind switch, shape) apply to
  // an OG preview card. Editing operations (duplicate / delete /
  // z-order / etc.) live on the unified `SelectionFloatingToolbar`
  // which renders below the selection regardless of kind.
  const toolbar = selected && !isEditing && kind !== 'link' && (
    <AnnotationToolbar itemId={itemId} kind={kind} color={color} shape={shape} />
  );

  // Eight-direction resize hit zones — invisible chrome that turns the
  // cursor into a `*-resize` glyph near the node edge and lets the user
  // drag any side or corner to resize. Skipped while editing so the
  // user can't accidentally start a resize while clicking through a
  // sticky to place the text caret.
  const resizeEdges = !isEditing && (
    <>
      <ResizeEdge
        position="t"
        onPointerDown={(e) => resize.beginResize(e, 't')}
        onPointerMove={resize.onResizeMove}
        onPointerUp={resize.endResize}
        onPointerCancel={resize.endResize}
      />
      <ResizeEdge
        position="b"
        onPointerDown={(e) => resize.beginResize(e, 'b')}
        onPointerMove={resize.onResizeMove}
        onPointerUp={resize.endResize}
        onPointerCancel={resize.endResize}
      />
      <ResizeEdge
        position="l"
        onPointerDown={(e) => resize.beginResize(e, 'l')}
        onPointerMove={resize.onResizeMove}
        onPointerUp={resize.endResize}
        onPointerCancel={resize.endResize}
      />
      <ResizeEdge
        position="r"
        onPointerDown={(e) => resize.beginResize(e, 'r')}
        onPointerMove={resize.onResizeMove}
        onPointerUp={resize.endResize}
        onPointerCancel={resize.endResize}
      />
      <ResizeEdge
        position="tl"
        onPointerDown={(e) => resize.beginResize(e, 'tl')}
        onPointerMove={resize.onResizeMove}
        onPointerUp={resize.endResize}
        onPointerCancel={resize.endResize}
      />
      <ResizeEdge
        position="tr"
        onPointerDown={(e) => resize.beginResize(e, 'tr')}
        onPointerMove={resize.onResizeMove}
        onPointerUp={resize.endResize}
        onPointerCancel={resize.endResize}
      />
      <ResizeEdge
        position="bl"
        onPointerDown={(e) => resize.beginResize(e, 'bl')}
        onPointerMove={resize.onResizeMove}
        onPointerUp={resize.endResize}
        onPointerCancel={resize.endResize}
      />
      <ResizeEdge
        position="br"
        onPointerDown={(e) => resize.beginResize(e, 'br')}
        onPointerMove={resize.onResizeMove}
        onPointerUp={resize.endResize}
        onPointerCancel={resize.endResize}
      />
    </>
  );

  // ---------------------------------------------------------------------
  // Link preview
  // ---------------------------------------------------------------------
  //
  // Spawned by URL paste (`components/workspace/paste-handler.tsx`). The
  // node lifecycle:
  //   1. paste-handler creates an annotation with
  //      `cache = { kind: 'link', url, linkStatus: 'pending' }` at the
  //      cursor. The node mounts immediately so the user sees a card
  //      with the bare URL while we wait on the OG fetch.
  //   2. This branch's `useEffect` fires `/api/og?url=…` once per
  //      itemId+url pair, then `patchItemState` writes the resolved
  //      title / description / image / siteName into `cache` and flips
  //      `linkStatus` to 'ready' (or 'error' on failure).
  //   3. Subsequent renders paint the OG card from cache only — no
  //      re-fetch on every mount. A right-click "Refresh preview"
  //      action (registered via the unified command registry as a
  //      future enhancement) can reset `linkStatus` to 'pending' to
  //      re-trigger the fetch.
  //
  // The card itself is a self-contained anchor: single click opens
  // the link in a new tab (we suppress this while RF is mid-drag so
  // the user can reposition without accidentally navigating). The
  // surrounding wrapper is the RF drag handle.
  if (kind === 'link') {
    return (
      <LinkAnnotation
        itemId={itemId}
        cache={cache}
        selected={!!selected}
        styleVars={styleVars}
        editingRing={editingRing}
        selectionRing={selectionRing}
        resizeEdges={resizeEdges}
        toolbar={toolbar}
        patchItemState={patchItemState}
      />
    );
  }

  // ---------------------------------------------------------------------
  // Sticky
  // ---------------------------------------------------------------------
  //
  // Layered structure:
  //   - Outer node wrapper: `overflow: visible` so connection handles
  //     that extend past the card edge are NOT clipped. This is the
  //     element React Flow injects, plus our drag-handle classes.
  //   - Inner clip wrapper: holds all the visual chrome (paper tint,
  //     highlight, dark-theme override, text) and applies the
  //     `rounded-none overflow-hidden` clip — sticky notes intentionally
  //     keep square paper corners (rounded corners read as a UI card
  //     and lose the "paper" affordance).
  //   - Sibling handles + rings: rendered OUTSIDE the clip so the
  //     cardinal connector dots straddle the card edge cleanly.
  if (kind === 'sticky') {
    return (
      <div
        className={cn(
          'group ws-annotation ws-annotation-sticky relative h-full w-full',
          'ws-node-drag-handle cursor-grab active:cursor-grabbing',
          // Drop shadow lives on the outer (un-clipped) wrapper so the
          // shadow paints around the card rather than getting cropped.
          'shadow-[0_1px_1px_rgba(15,23,42,0.06),0_8px_18px_-10px_rgba(15,23,42,0.22)]',
          'dark:shadow-[0_1px_1px_rgba(0,0,0,0.4),0_10px_22px_-10px_rgba(0,0,0,0.55)]',
          // Square corners — sticky notes in Figjam/Confluence keep the
          // paper rectangle uniform; rounded corners read as a UI card
          // and lose the "paper" affordance.
          'rounded-none',
        )}
        style={styleVars}
        onDoubleClick={(e) => {
          e.stopPropagation();
          enterEdit();
        }}
      >
        {/* Clipped visual layer. The square corners + overflow:hidden
            stay on THIS element only — handles sit outside it. */}
        <div className="absolute inset-0 rounded-none overflow-hidden">
          <div
            className="absolute inset-0 transition-colors"
            style={{ background: 'var(--ann-bg)' }}
          />
          <div
            aria-hidden
            className="absolute inset-x-0 top-0 h-1/3 pointer-events-none opacity-50 dark:opacity-25"
            style={{
              background:
                'linear-gradient(180deg, rgba(255,255,255,0.7), rgba(255,255,255,0))',
            }}
          />
          <div
            aria-hidden
            className="absolute inset-0 hidden dark:block"
            style={{ background: 'var(--ann-bg-dark)' }}
          />
          {/* The editor surface inherits color from this container.
              `ws-annotation-fg` resolves to `var(--ann-fg)` on light
              and `var(--ann-fg-dark)` on dark — without it, inline
              `color: var(--ann-fg)` would pin the text to the light
              palette and the warm brown/orange foreground was
              unreadable against the dark-themed background. */}
          <div
            className={cn(
              'absolute inset-0 p-3 overflow-auto nowheel ws-annotation-fg',
            )}
          >
            <div className="relative h-full w-full">{editorSurface}</div>
          </div>
        </div>
        {editingRing}
        {selectionRing}
        {/* Resize via invisible edge hit zones — see `ResizeEdge`. The
            sticky exposes all eight directions so users can grow it
            from any side without first selecting it. */}
        {resizeEdges}
        <AnnotationHandles />
        {toolbar}
      </div>
    );
  }

  // ---------------------------------------------------------------------
  // Text
  // ---------------------------------------------------------------------
  if (kind === 'text') {
    return (
      <div
        className={cn(
          'group ws-annotation ws-annotation-text relative h-full w-full',
          'ws-node-drag-handle cursor-grab active:cursor-grabbing',
          // Drives `color` via CSS var lookups that switch on `.dark`.
          'ws-annotation-fg',
        )}
        style={styleVars}
        onDoubleClick={(e) => {
          e.stopPropagation();
          enterEdit();
        }}
      >
        <div className="absolute inset-0 px-1 py-0.5 overflow-hidden">
          {editorSurface}
        </div>
        {editingRing}
        {selectionRing}
        {resizeEdges}
        <AnnotationHandles />
        {toolbar}
      </div>
    );
  }

  // ---------------------------------------------------------------------
  // Shape
  // ---------------------------------------------------------------------
  // SVG primitive scaled to the node bounding box. Diamond and triangle
  // are stretched paths so they fill the box; rect/ellipse use the
  // primitives directly. Optional text rides in a <foreignObject> so it
  // wraps and selects like normal HTML.
  return (
    <ShapeAnnotation
      shape={shape}
      stroke={stroke}
      style={styleVars}
      isEditing={isEditing}
      selected={!!selected}
      onDoubleClick={enterEdit}
      resizeEdges={resizeEdges}
      toolbar={toolbar}
    >
      <div
        className={cn(
          'h-full w-full p-2',
          // Theme-aware text color — see comment on the sticky variant.
          'ws-annotation-fg',
        )}
      >
        {editorSurface}
      </div>
    </ShapeAnnotation>
  );
}

function ShapeAnnotation({
  shape,
  stroke,
  style,
  selected,
  isEditing,
  onDoubleClick,
  children,
  resizeEdges,
  toolbar,
}: {
  shape: AnnotationShape;
  stroke: AnnotationStroke;
  style: CSSProperties;
  selected: boolean;
  isEditing: boolean;
  onDoubleClick: () => void;
  children: React.ReactNode;
  resizeEdges: React.ReactNode;
  toolbar: React.ReactNode;
}) {
  // When the user picks "No border" we drive the SVG `stroke` to
  // `transparent`. We keep the stroke attribute present so the
  // primitive's painted area still includes the perimeter — that
  // way pointer events fire across the entire visible shape, not
  // only the filled interior.
  const strokeLight = strokeColor(stroke, 'var(--ann-ring)');
  const strokeDark = strokeColor(stroke, 'var(--ann-ring-dark)');
  // Use a ref to read the rendered size so the SVG primitive scales 1:1
  // with the node's current bounding box. We don't need a ResizeObserver
  // — RF re-renders the node on every dimension change anyway, and a
  // single `getBoundingClientRect` read per render is cheap. We fall
  // back to 100% percentage units which works in every modern browser
  // for SVG inside flexbox.
  const dashArray = strokeDashArray(stroke);
  const containerRef = useRef<HTMLDivElement | null>(null);

  // Two SVG-wrapping strategies live side-by-side:
  //
  //   - Ellipse / diamond / triangle use a fixed `0 0 100 100` viewBox
  //     plus `preserveAspectRatio="none"` so the primitive STRETCHES to
  //     fill the node bounding box (Figma + Excalidraw use the same
  //     trick for resizable polygons).
  //
  //   - Rectangle uses NO viewBox so user-space units equal CSS pixels.
  //     That way `rx="6"` resolves to a literal 6px radius — independent
  //     of aspect ratio. The shared 100x100 viewBox path made `rx=6`
  //     resolve to "6% width × 6% height" instead, which squished
  //     corners into ellipses on non-square rectangles.
  //
  // The rectangle rect sits 1px inside the SVG box (via CSS geometry
  // properties so we can use `calc()` — SVG attributes can't) which
  // mirrors the 1px inset used for the other primitives so a 1.6px
  // stroke renders without clipping at the parent's edge.
  const RECT_STYLE = useMemo<CSSProperties>(
    () => ({
      x: '1px',
      y: '1px',
      width: 'calc(100% - 2px)',
      height: 'calc(100% - 2px)',
    }),
    [],
  );

  const primitiveLight = useMemo(() => {
    switch (shape) {
      case 'ellipse':
        return (
          <svg
            className="absolute inset-0 h-full w-full"
            viewBox="0 0 100 100"
            preserveAspectRatio="none"
            aria-hidden
          >
            <ellipse
              cx="50"
              cy="50"
              rx="49"
              ry="49"
              fill="var(--ann-bg)"
              stroke={strokeLight}
              strokeWidth="1.6"
              strokeDasharray={dashArray}
              vectorEffect="non-scaling-stroke"
            />
          </svg>
        );
      case 'diamond':
        return (
          <svg
            className="absolute inset-0 h-full w-full"
            viewBox="0 0 100 100"
            preserveAspectRatio="none"
            aria-hidden
          >
            <polygon
              points="50,1 99,50 50,99 1,50"
              fill="var(--ann-bg)"
              stroke={strokeLight}
              strokeWidth="1.6"
              strokeDasharray={dashArray}
              vectorEffect="non-scaling-stroke"
            />
          </svg>
        );
      case 'triangle':
        return (
          <svg
            className="absolute inset-0 h-full w-full"
            viewBox="0 0 100 100"
            preserveAspectRatio="none"
            aria-hidden
          >
            <polygon
              points="50,2 99,98 1,98"
              fill="var(--ann-bg)"
              stroke={strokeLight}
              strokeWidth="1.6"
              strokeDasharray={dashArray}
              vectorEffect="non-scaling-stroke"
            />
          </svg>
        );
      case 'rectangle':
      default:
        return (
          <svg className="absolute inset-0 h-full w-full" aria-hidden>
            <rect
              rx="6"
              ry="6"
              fill="var(--ann-bg)"
              stroke={strokeLight}
              strokeWidth="1.6"
              strokeDasharray={dashArray}
              vectorEffect="non-scaling-stroke"
              style={RECT_STYLE}
            />
          </svg>
        );
    }
  }, [shape, dashArray, strokeLight, RECT_STYLE]);

  const primitiveDark = useMemo(() => {
    switch (shape) {
      case 'ellipse':
        return (
          <svg
            className="absolute inset-0 h-full w-full hidden dark:block"
            viewBox="0 0 100 100"
            preserveAspectRatio="none"
            aria-hidden
          >
            <ellipse
              cx="50"
              cy="50"
              rx="49"
              ry="49"
              fill="var(--ann-bg-dark)"
              stroke={strokeDark}
              strokeWidth="1.6"
              strokeDasharray={dashArray}
              vectorEffect="non-scaling-stroke"
            />
          </svg>
        );
      case 'diamond':
        return (
          <svg
            className="absolute inset-0 h-full w-full hidden dark:block"
            viewBox="0 0 100 100"
            preserveAspectRatio="none"
            aria-hidden
          >
            <polygon
              points="50,1 99,50 50,99 1,50"
              fill="var(--ann-bg-dark)"
              stroke={strokeDark}
              strokeWidth="1.6"
              strokeDasharray={dashArray}
              vectorEffect="non-scaling-stroke"
            />
          </svg>
        );
      case 'triangle':
        return (
          <svg
            className="absolute inset-0 h-full w-full hidden dark:block"
            viewBox="0 0 100 100"
            preserveAspectRatio="none"
            aria-hidden
          >
            <polygon
              points="50,2 99,98 1,98"
              fill="var(--ann-bg-dark)"
              stroke={strokeDark}
              strokeWidth="1.6"
              strokeDasharray={dashArray}
              vectorEffect="non-scaling-stroke"
            />
          </svg>
        );
      case 'rectangle':
      default:
        return (
          <svg className="absolute inset-0 h-full w-full hidden dark:block" aria-hidden>
            <rect
              rx="6"
              ry="6"
              fill="var(--ann-bg-dark)"
              stroke={strokeDark}
              strokeWidth="1.6"
              strokeDasharray={dashArray}
              vectorEffect="non-scaling-stroke"
              style={RECT_STYLE}
            />
          </svg>
        );
    }
  }, [shape, dashArray, strokeDark, RECT_STYLE]);

  // Selection chrome / editing ring need to match the rectangle's
  // pixel-radius. Other primitives are pure SVG so their visual edge
  // is already drawn — a square selection box around a diamond /
  // ellipse / triangle reads as the bounding box, not as a second
  // shape, so they keep `rounded=0` (sharp blue bbox).
  const isRect = shape === 'rectangle';
  const editingRingClass = isRect
    ? 'absolute inset-0 pointer-events-none rounded-[6px]'
    : 'absolute inset-0 pointer-events-none rounded-[inherit]';

  return (
    <div
      ref={containerRef}
      className={cn(
        'group ws-annotation ws-annotation-shape relative h-full w-full',
        'ws-node-drag-handle cursor-grab active:cursor-grabbing',
      )}
      style={style}
      onDoubleClick={(e) => {
        e.stopPropagation();
        onDoubleClick();
      }}
    >
      {primitiveLight}
      {/* Dark-theme palette override — paint a same-shape SVG layer on
          top using the dark CSS variable. */}
      {primitiveDark}
      <div className="relative h-full w-full">{children}</div>
      {isEditing && (
        <div
          aria-hidden
          className={editingRingClass}
          style={{
            boxShadow:
              '0 0 0 2px color-mix(in srgb, var(--ann-ring) 70%, transparent)',
          }}
        />
      )}
      {selected && !isEditing && (
        <AnnotationSelectionChrome rounded={isRect ? 6 : 0} />
      )}
      {resizeEdges}
      <AnnotationHandles />
      {toolbar}
    </div>
  );
}

/**
 * Memoized export — RF re-renders every node on every store tick, but the
 * annotation body only depends on its own item state + the selection /
 * dragging flags. Custom equality keeps node-local renders cheap when
 * unrelated items change.
 */
export const AnnotationNode = memo(AnnotationNodeImpl, (a, b) => {
  if (a.id !== b.id) return false;
  if (a.selected !== b.selected) return false;
  if (a.dragging !== b.dragging) return false;
  // The `data` object identity is stable across renders because the
  // canvas only stuffs `{ itemId }` in there — store-driven changes
  // arrive via the `useWorkspaceItem` hook below, not via `data`.
  return true;
});

// -----------------------------------------------------------------------
// LinkAnnotation — `cache.kind === 'link'`
//
// OpenGraph link preview card. Self-contained because the fetch
// lifecycle (pending → ready / error) is local to this node and
// would clutter the main annotation switch.
//
// Render contract:
//   - First paint: skeleton card with the URL's hostname + a soft
//     pulse on the image slot while we wait for OG metadata.
//   - `cache.linkStatus === 'ready'`: full OG card (image, title,
//     description, site name + hostname row).
//   - `cache.linkStatus === 'error'`: minimal "link" card with the
//     URL displayed prominently and an "Open" affordance. The user
//     can still drag / delete / right-click → Refresh.
//
// Why a separate inner component rather than inlining: the OG fetch
// `useEffect` would otherwise run inside the giant `AnnotationNodeImpl`
// body alongside all the contenteditable plumbing. Extracting it
// keeps the deps array tight (just `itemId` + `cache.url`) and means
// the fetch doesn't churn on every sticky / text / shape render.
// -----------------------------------------------------------------------

type LinkAnnotationProps = {
  itemId: string;
  cache: NonNullable<WorkspaceItemDTO['state']['cache']>;
  selected: boolean;
  styleVars: CSSProperties;
  editingRing: React.ReactNode;
  selectionRing: React.ReactNode;
  resizeEdges: React.ReactNode;
  toolbar: React.ReactNode;
  patchItemState: (
    id: string,
    patch: Partial<WorkspaceItemDTO['state']>,
    opts?: { debounceMs?: number; persist?: boolean },
  ) => void;
};

function LinkAnnotation({
  itemId,
  cache,
  selected,
  styleVars,
  editingRing,
  selectionRing,
  resizeEdges,
  toolbar,
  patchItemState,
}: LinkAnnotationProps) {
  const t = useT();
  const url = (cache.url as string | undefined) ?? '';
  const linkStatus = (cache.linkStatus as 'pending' | 'ready' | 'error' | undefined) ?? 'pending';
  const title = (cache.title as string | undefined) ?? '';
  const description = (cache.description as string | undefined) ?? '';
  const image = (cache.image as string | undefined) ?? '';
  const siteName = (cache.siteName as string | undefined) ?? '';

  // Derive hostname once for the chrome row + the skeleton title.
  // Falls back to the raw URL if parsing fails (e.g. truncated paste).
  const hostname = useMemo(() => {
    try {
      return new URL(url).hostname.replace(/^www\./, '');
    } catch {
      return url.slice(0, 60);
    }
  }, [url]);

  // Fetch OG metadata on first mount / when the URL changes / when a
  // pending status is set externally (e.g. user clicked "Refresh").
  // We deliberately don't refetch on 'ready' — once we have a card,
  // refreshes are explicit.
  useEffect(() => {
    if (!url) return;
    if (linkStatus !== 'pending') return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`/api/og?url=${encodeURIComponent(url)}`);
        if (!res.ok) {
          if (cancelled) return;
          patchItemState(itemId, {
            cache: { ...cache, linkStatus: 'error' },
          });
          return;
        }
        const data = (await res.json()) as {
          title?: string;
          description?: string;
          image?: string;
          imageLocal?: string;
          siteName?: string;
        };
        if (cancelled) return;
        patchItemState(itemId, {
          cache: {
            ...cache,
            linkStatus: 'ready',
            title: data.title ?? hostname,
            description: data.description ?? '',
            image: data.imageLocal ?? data.image ?? '',
            siteName: data.siteName ?? hostname,
          },
        });
      } catch {
        if (cancelled) return;
        patchItemState(itemId, {
          cache: { ...cache, linkStatus: 'error' },
        });
      }
    })();
    return () => {
      cancelled = true;
    };
    // We intentionally don't list `cache` / `patchItemState` so the
    // effect only fires on initial pending → ready/error. Adding
    // `cache` would re-trigger the fetch every time we patch it
    // (which is the very thing the fetch does), producing an
    // infinite loop. Re-fetch is opt-in: set linkStatus back to
    // 'pending' explicitly.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemId, url, linkStatus]);

  // Open in a new tab on click — but only if the user isn't currently
  // dragging. RF's `dragging` flag is exposed through the node props
  // up the tree; here we conservatively check `pointermove > 4px`
  // between pointerdown and click via a ref.
  const dragGuard = useRef<{ x: number; y: number } | null>(null);
  const onPointerDown = useCallback((e: React.PointerEvent) => {
    dragGuard.current = { x: e.clientX, y: e.clientY };
  }, []);
  const onClick = useCallback(
    (e: React.MouseEvent) => {
      const start = dragGuard.current;
      dragGuard.current = null;
      if (!start) return;
      const dx = e.clientX - start.x;
      const dy = e.clientY - start.y;
      if (Math.hypot(dx, dy) > 4) return;
      if (!url) return;
      e.stopPropagation();
      window.open(url, '_blank', 'noopener,noreferrer');
    },
    [url],
  );

  const isReady = linkStatus === 'ready';
  const isPending = linkStatus === 'pending';
  const showImage = isReady && !!image;

  // Embed action — visible only when the URL maps to an entry in the
  // iframe-embed registry (YouTube, PlayCanvas, CodePen, …). Clicking
  // calls the same /api/widgets/import-from-url pipeline that the
  // paste-handler uses, spawns a content widget at the same canvas
  // location, then removes this link annotation. The card's normal
  // click-to-open-in-tab handler is suppressed for the button.
  const embed = useMemo(() => lookupIframeEmbed(url), [url]);
  const { addItem, removeItem } = useWorkspaceActions();
  const [embedding, setEmbedding] = useState(false);
  const onEmbedClick = useCallback(
    async (e: React.MouseEvent) => {
      e.stopPropagation();
      if (!embed || embedding) return;
      setEmbedding(true);
      try {
        const imported = await importUrlAsExternalWidget(embed.embedUrl);
        if (!imported) {
          setEmbedding(false);
          return;
        }
        // Spawn the iframe widget then delete the link card.
        // Placement = 'viewport' so findEmptySpace lands the new tile
        // somewhere visible (the card we're replacing is in-viewport
        // by definition since the user just clicked its button).
        // Sizing from the registry's aspect ratio: video → 16:9,
        // code playgrounds → 4:3, etc.
        const width = 560;
        const height = Math.round(width / (embed.aspectRatio ?? 16 / 9));
        await addItem({
          type: 'content',
          refId: imported.widgetContentId,
          state: {
            width,
            height,
            cache: { displayName: embed.siteName, contentType: 'widget' },
          },
        }).catch(() => {});
        await removeItem(itemId).catch(() => {});
      } finally {
        setEmbedding(false);
      }
    },
    [embed, embedding, addItem, removeItem, itemId],
  );

  return (
    <div
      className={cn(
        'group ws-annotation ws-annotation-link relative h-full w-full',
        'ws-node-drag-handle cursor-grab active:cursor-grabbing',
        'rounded-lg overflow-hidden',
        'border border-border/70 bg-card text-card-foreground',
        'shadow-[0_1px_2px_rgba(15,23,42,0.06),0_8px_18px_-12px_rgba(15,23,42,0.20)]',
        'dark:shadow-[0_1px_2px_rgba(0,0,0,0.4),0_10px_22px_-12px_rgba(0,0,0,0.55)]',
      )}
      style={styleVars}
      onPointerDown={onPointerDown}
      onClick={onClick}
      title={url}
    >
      <div className="flex h-full w-full flex-col">
        {showImage && (
          <div
            className="relative w-full shrink-0 overflow-hidden bg-muted"
            style={{ aspectRatio: '1.91 / 1' }}
          >
            <img
              src={image}
              alt={title || hostname}
              draggable={false}
              className="h-full w-full object-cover"
              onError={(e) => {
                (e.currentTarget as HTMLImageElement).style.display = 'none';
              }}
            />
          </div>
        )}
        {isPending && !image && (
          <div
            className="w-full shrink-0 bg-muted animate-pulse"
            style={{ aspectRatio: '1.91 / 1' }}
            aria-hidden
          />
        )}
        <div className="flex flex-1 min-h-0 flex-col gap-1 p-3">
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground/80 truncate">
            {siteName || hostname}
          </div>
          <div
            className={cn(
              'text-sm font-semibold leading-snug line-clamp-2',
              isPending && !title && 'text-muted-foreground',
            )}
          >
            {title || (isPending ? 'Loading preview…' : hostname)}
          </div>
          {description && (
            <div className="text-xs text-muted-foreground leading-snug line-clamp-3">
              {description}
            </div>
          )}
          {!description && linkStatus === 'error' && (
            <div className="text-xs text-muted-foreground leading-snug">
              {t('workspace.node.previewUnavailable')}
            </div>
          )}
          <div className="mt-auto flex items-center justify-between gap-2 pt-1 text-[10px] text-muted-foreground/80">
            <span className="truncate">{hostname}</span>
            {embed && (
              <button
                type="button"
                onClick={onEmbedClick}
                onPointerDown={(e) => e.stopPropagation()}
                disabled={embedding}
                className={cn(
                  'shrink-0 rounded-md border border-border/70 bg-background/80 px-2 py-0.5 text-[10px] font-medium',
                  'text-foreground/80 hover:bg-accent hover:text-accent-foreground',
                  'transition-colors disabled:opacity-50 disabled:hover:bg-background/80',
                )}
                title={`Replace this card with a live ${embed.siteName} iframe`}
              >
                {embedding ? 'Embedding…' : `Embed as ${embed.siteName}`}
              </button>
            )}
          </div>
        </div>
      </div>
      {editingRing}
      {selectionRing || (selected && <AnnotationSelectionChrome rounded={8} />)}
      {resizeEdges}
      <AnnotationHandles />
      {toolbar}
    </div>
  );
}
